import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import {
  BarChart3,
  MessageSquare,
  MousePointerClick,
  ShoppingBag,
  FlaskConical,
  TrendingUp,
  Globe,
  Smartphone,
  Eye,
  ShoppingCart,
  CreditCard,
  Share2,
  MapPin,
} from 'lucide-react';

interface DailyPoint {
  day: string;
  count: number;
}

interface SummaryData {
  days: number;
  webStats: {
    pageView: number;
    productView: number;
    addToCart: number;
    beginCheckout: number;
    orderCreated: number;
    whatsappClick: number;
    referralLinkClick: number;
  };
  whatsappStats: {
    messageSent: number;
    byTemplate: Array<{ template: string; count: number }>;
  };
  daily: Record<string, DailyPoint[]>;
  abTests: any[];
}

interface Hotspot {
  estate: string;
  district: string;
  estateType: string;
  orderCount: number;
  totalAmount: number;
  lastOrderedAt: string;
}

const TABS = [
  { id: 'web', label: '網頁數據', icon: Globe },
  { id: 'whatsapp', label: 'WhatsApp 數據', icon: Smartphone },
  { id: 'hotspots', label: '潛在開店熱點', icon: MapPin },
];

function Sparkline({ data, color = '#C2410C' }: { data: DailyPoint[]; color?: string }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-stone-400 text-sm">暫無足夠數據</div>
    );
  }
  const max = Math.max(...data.map((x) => x.count), 1);
  return (
    <div className="flex items-end gap-2 h-48">
      {data.map((d) => {
        const height = `${(d.count / max) * 100}%`;
        return (
          <div key={d.day} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <div
              className="w-full rounded-t-md min-h-[4px]"
              style={{ height, backgroundColor: color }}
              title={`${d.day}: ${d.count}`}
            />
            <span className="text-[10px] text-stone-400 truncate w-full text-center">{d.day.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

const AdminAnalytics: React.FC = () => {
  const [data, setData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(7);
  const [activeTab, setActiveTab] = useState<'web' | 'whatsapp' | 'hotspots'>('web');
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [hotspotsLoading, setHotspotsLoading] = useState(false);
  const [hotspotsError, setHotspotsError] = useState('');

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/public/admin/analytics/summary?days=${days}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) setError(json.error);
        else setData(json);
      })
      .catch(() => setError('載入失敗'))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    if (activeTab !== 'hotspots') return;
    setHotspotsLoading(true);
    setHotspotsError('');
    apiFetch('/api/public/admin/analytics/direct-delivery-hotspots')
      .then((res) => res.json())
      .then((json) => {
        if (json.error) setHotspotsError(json.error);
        else if (json.success) setHotspots(json.data || []);
        else setHotspotsError('載入失敗');
      })
      .catch(() => setHotspotsError('載入失敗'))
      .finally(() => setHotspotsLoading(false));
  }, [activeTab]);

  const webCards = data
    ? [
        { label: '頁面瀏覽', value: data.webStats.pageView, icon: Eye, color: 'text-purple-600', bg: 'bg-purple-50' },
        { label: '產品瀏覽', value: data.webStats.productView, icon: MousePointerClick, color: 'text-indigo-600', bg: 'bg-indigo-50' },
        { label: '加入購物車', value: data.webStats.addToCart, icon: ShoppingCart, color: 'text-amber-600', bg: 'bg-amber-50' },
        { label: '開始結帳', value: data.webStats.beginCheckout, icon: CreditCard, color: 'text-pink-600', bg: 'bg-pink-50' },
        { label: '訂單建立', value: data.webStats.orderCreated, icon: ShoppingBag, color: 'text-green-600', bg: 'bg-green-50' },
        { label: 'WhatsApp 點擊', value: data.webStats.whatsappClick, icon: MessageSquare, color: 'text-blue-600', bg: 'bg-blue-50' },
        { label: '推薦連結點擊', value: data.webStats.referralLinkClick, icon: Share2, color: 'text-orange-600', bg: 'bg-orange-50' },
      ]
    : [];

  const conversionRate = (numerator: number, denominator: number) =>
    denominator > 0 ? ((numerator / denominator) * 100).toFixed(1) : '0.0';

  const formatCurrency = (amount: number) =>
    `HK$${amount.toLocaleString('zh-HK')}`;

  const formatDateTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('zh-HK');
    } catch {
      return iso;
    }
  };

  const renderHotspotsTab = () => {
    if (hotspotsLoading) {
      return <div className="text-center py-20 text-stone-400">載入中...</div>;
    }
    if (hotspotsError) {
      return <div className="text-center py-20 text-red-600">{hotspotsError}</div>;
    }
    if (hotspots.length === 0) {
      return <div className="text-center py-20 text-stone-400">暫無直送訂單數據</div>;
    }
    return (
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-stone-50 text-stone-600 font-semibold">
              <tr>
                <th className="px-4 py-3">屋苑</th>
                <th className="px-4 py-3">地區</th>
                <th className="px-4 py-3">類型</th>
                <th className="px-4 py-3 text-right">直送單數</th>
                <th className="px-4 py-3 text-right">總金額</th>
                <th className="px-4 py-3">最後落單時間</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {hotspots.map((item, idx) => (
                <tr key={`${item.estate}-${item.district}-${idx}`} className="hover:bg-stone-50">
                  <td className="px-4 py-3 font-medium text-stone-800">{item.estate}</td>
                  <td className="px-4 py-3 text-stone-600">{item.district}</td>
                  <td className="px-4 py-3 text-stone-600">{item.estateType}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#C2410C]">
                    {item.orderCount.toLocaleString('zh-HK')}
                  </td>
                  <td className="px-4 py-3 text-right text-stone-800">{formatCurrency(item.totalAmount)}</td>
                  <td className="px-4 py-3 text-stone-500">{formatDateTime(item.lastOrderedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout currentPage="數據分析">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-stone-800 flex items-center gap-2">
              <BarChart3 className="text-[#C2410C]" />
              數據分析
            </h1>
            <p className="text-stone-500 mt-1">追蹤客戶旅程與推廣成效，用數據驅動決策。</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-500">時間範圍</span>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C2410C]"
            >
              <option value={7}>過去 7 天</option>
              <option value={30}>過去 30 天</option>
              <option value={90}>過去 90 天</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8 border-b border-stone-200">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'web' | 'whatsapp' | 'hotspots')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  active
                    ? 'border-[#C2410C] text-[#C2410C]'
                    : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-20 text-stone-400">載入中...</div>
        ) : error ? (
          <div className="text-center py-20 text-red-600">{error}</div>
        ) : (
          <div className="space-y-8">
            {activeTab === 'web' && data && (
              <>
                {/* Web stat cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {webCards.map((card) => (
                    <div key={card.label} className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm">
                      <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center mb-3`}>
                        <card.icon className={card.color} size={20} />
                      </div>
                      <p className="text-3xl font-bold text-stone-800">{card.value.toLocaleString()}</p>
                      <p className="text-sm text-stone-500">{card.label}</p>
                    </div>
                  ))}
                </div>

                {/* Conversion funnel */}
                <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
                  <h2 className="text-lg font-bold text-stone-800 mb-6">轉化漏斗</h2>
                  <div className="space-y-4">
                    {[
                      { label: '產品瀏覽', value: data.webStats.productView, prev: data.webStats.pageView },
                      { label: '加入購物車', value: data.webStats.addToCart, prev: data.webStats.productView },
                      { label: '開始結帳', value: data.webStats.beginCheckout, prev: data.webStats.addToCart },
                      { label: '完成訂單', value: data.webStats.orderCreated, prev: data.webStats.beginCheckout },
                    ].map((step, idx) => (
                      <div key={step.label}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-stone-700">
                            {idx + 1}. {step.label}
                          </span>
                          <span className="text-sm font-bold text-stone-800">
                            {step.value.toLocaleString()}
                            {step.prev > 0 && (
                              <span className="text-xs font-normal text-stone-400 ml-2">
                                ({conversionRate(step.value, step.prev)}%)
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#C2410C] rounded-full"
                            style={{ width: `${Math.min((step.value / Math.max(step.prev, 1)) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Page view trend */}
                <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="text-[#C2410C]" size={20} />
                    <h2 className="text-lg font-bold text-stone-800">頁面瀏覽趨勢</h2>
                  </div>
                  <Sparkline data={data.daily['page_view'] || []} color="#9333ea" />
                </div>
              </>
            )}

            {activeTab === 'whatsapp' && data && (
              <>
                {/* WhatsApp stat cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-3">
                      <MessageSquare className="text-blue-600" size={20} />
                    </div>
                    <p className="text-3xl font-bold text-stone-800">{data.whatsappStats.messageSent.toLocaleString()}</p>
                    <p className="text-sm text-stone-500">WhatsApp 發送總數</p>
                  </div>
                </div>

                {/* Message sent by template */}
                <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
                  <h2 className="text-lg font-bold text-stone-800 mb-4">按模板發送量</h2>
                  {data.whatsappStats.byTemplate.length > 0 ? (
                    <div className="space-y-3">
                      {data.whatsappStats.byTemplate.map((t) => (
                        <div key={t.template} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                          <span className="text-sm font-medium text-stone-700">{t.template}</span>
                          <span className="text-sm font-bold text-[#C2410C]">{t.count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-stone-400 text-sm">暫無足夠數據</div>
                  )}
                </div>

                {/* Message sent trend */}
                <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="text-[#C2410C]" size={20} />
                    <h2 className="text-lg font-bold text-stone-800">WhatsApp 發送趨勢</h2>
                  </div>
                  <Sparkline data={data.daily['message_sent'] || []} color="#2563eb" />
                </div>
              </>
            )}

            {activeTab === 'hotspots' && renderHotspotsTab()}

            {/* A/B testing placeholder */}
            <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <FlaskConical className="text-[#C2410C]" size={20} />
                <h2 className="text-lg font-bold text-stone-800">A/B 測試（預留）</h2>
              </div>
              <div className="border-2 border-dashed border-stone-200 rounded-xl p-8 text-center text-stone-500">
                <p>未來可在此比較不同 WhatsApp 模板版本的點擊率與轉化率。</p>
                <p className="text-sm mt-2">例如：order_completion_thanks_v1 的 Button A vs Button B。</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
